'use client';

/**
 * RatingField — Star / NPS / emoji rating input.
 *
 * Config via field.options or field.metadata:
 *   max: number (default 5)
 *   style: 'star' | 'nps' | 'emoji' (default 'star')
 *   labels: { low?: string; high?: string }
 */

import React, { useState } from 'react';
import type { FieldComponentField, FieldValue } from '../formFieldTypes';

interface RatingFieldProps {
  field: FieldComponentField;
  value: FieldValue;
  onChange: (value: number | null) => void;
  isDark: boolean;
  readOnly?: boolean;
}

const EMOJI_SCALE = ['😞', '😐', '🙂', '😊', '🤩'];

export const RatingField: React.FC<RatingFieldProps> = ({ field, value, onChange, isDark, readOnly }) => {
  const meta = field.metadata as {
    max?: number;
    style?: 'star' | 'nps' | 'emoji';
    labels?: { low?: string; high?: string };
  } | undefined;
  const validation = field.validation as { max?: number } | undefined;
  const max = meta?.max || validation?.max || 5;
  const style: 'star' | 'nps' | 'emoji' = meta?.style || 'star';
  const lowLabel = meta?.labels?.low || '';
  const highLabel = meta?.labels?.high || '';
  const [hovered, setHovered] = useState<number | null>(null);

  const current = typeof value === 'number' ? value : null;

  // ── Star rating ───────────────────────────────────────────────
  if (style === 'star') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
            const filled = (hovered ?? current ?? 0) >= n;
            return (
              <button
                key={n}
                type="button"
                disabled={readOnly}
                onMouseEnter={() => !readOnly && setHovered(n)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onChange(current === n ? null : n)}
                className={`text-2xl transition-transform ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
                aria-label={`${n} star${n > 1 ? 's' : ''}`}
              >
                {filled ? '★' : '☆'}
              </button>
            );
          })}
          {current !== null && (
            <span className={`ml-2 text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {current}/{max}
            </span>
          )}
        </div>
        {(lowLabel || highLabel) && (
          <div className="flex justify-between text-[10px] opacity-60">
            <span>{lowLabel}</span>
            <span>{highLabel}</span>
          </div>
        )}
      </div>
    );
  }

  // ── Emoji rating ──────────────────────────────────────────────
  if (style === 'emoji') {
    const emojis = EMOJI_SCALE.slice(0, max);
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {emojis.map((emoji, i) => {
            const n = i + 1;
            const active = current === n;
            return (
              <button
                key={n}
                type="button"
                disabled={readOnly}
                onClick={() => onChange(active ? null : n)}
                className={`text-2xl transition-all rounded-full p-1 ${
                  active ? 'scale-125 ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/30' : ''
                } ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
              >
                {emoji}
              </button>
            );
          })}
        </div>
        {(lowLabel || highLabel) && (
          <div className="flex justify-between text-[10px] opacity-60">
            <span>{lowLabel}</span>
            <span>{highLabel}</span>
          </div>
        )}
      </div>
    );
  }

  // ── NPS (0–10 number row) ─────────────────────────────────────
  const npsMax = max > 10 ? max : 10;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: npsMax + 1 }, (_, i) => i).map((n) => {
          const active = current === n;
          const color =
            n <= 6
              ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
              : n <= 8
                ? 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700'
                : 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700';
          return (
            <button
              key={n}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(active ? null : n)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold border transition-all ${color} ${
                active ? 'ring-2 ring-blue-500 scale-110 shadow' : ''
              } ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-105'}`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] opacity-60">
        <span>{lowLabel || 'Not likely'}</span>
        <span>{highLabel || 'Very likely'}</span>
      </div>
    </div>
  );
};
